import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Car, ShieldAlert, CheckCircle, Info, ArrowRight, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Helmet } from 'react-helmet-async';

const BillejePage = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const tips = [
    {
      title: 'Bestil i god tid',
      description: 'Priserne på billeje stiger ofte, jo tættere du kommer på afrejsedatoen. Det betaler sig at booke tidligt.'
    },
    {
      title: 'Kreditkort i førerens navn',
      description: 'Husk at biludlejningsfirmaet altid kræver et fysisk kreditkort (ikke debetkort) udstedt i hovedførerens navn til depositum.'
    },
    {
      title: 'Tjek forsikringsbetingelser',
      description: 'Vi anbefaler altid at tegne en fuld kaskoforsikring (SCDW) uden selvrisiko for at undgå ubehagelige overraskelser.'
    },
    {
      title: 'Brændstofpolitik',
      description: 'Vælg så vidt muligt "fuld-til-fuld" (full-to-full), så du kun betaler for det brændstof, du rent faktisk bruger.'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Billeje | Golf4Life - Information om biludlejning</title>
        <meta name="description" content="Vigtig information vedrørende billeje til dit golfophold i Spanien. Læs vores retningslinjer og gode råd til reservation af bil." />
      </Helmet>

      <Navbar solid={true} />

      <main className="pt-20 md:pt-24">
        {/* Hero Section */}
        <section className="relative h-[40vh] md:h-[50vh] min-h-[350px] overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/e2629140-c2a2-40f1-a977-6c6dd158f23f.jpg" 
              alt="Billeje i Spanien" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-transparent" />
          </div>

          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-2xl text-white"
              >
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white text-sm font-medium mb-4 backdrop-blur-sm">
                  <Car size={16} />
                  Transport i Spanien
                </span>
                <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4">
                  Billeje til dit golfophold
                </h1>
                <p className="text-lg text-slate-200 mb-8">
                  Her finder du vigtig information og gode råd til reservation af din lejebil under dit ophold i Cádiz.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link 
                    to="/kontakt" 
                    className="inline-flex items-center justify-center px-6 py-3 md:px-8 md:py-4 rounded-full border-2 border-white bg-transparent text-white font-semibold text-base md:text-lg hover:-translate-y-1 transition-all shadow-lg shadow-black/20 group"
                  >
                    Book dit ophold
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Important Disclaimer Section */}
        <section className="py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6 md:p-10 shadow-md relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 p-4 opacity-5 text-amber-900 pointer-events-none">
                <ShieldAlert size={120} />
              </div>
              
              <div className="flex gap-4 md:gap-6 items-start">
                <div className="p-3 bg-amber-100 text-amber-800 rounded-xl shrink-0">
                  <ShieldAlert size={28} />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-semibold text-amber-900 mb-4">
                    Vigtig information vedrørende billeje
                  </h2>
                  <p className="text-base md:text-lg text-amber-800 leading-relaxed font-medium">
                    Vær venligst opmærksom på at du lejer ikke din bil igennem Golf4life, men bilfirmaet, vi henviser kun og du skal selv stå for reservation af bil.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Discount Partners Section */}
        <section className="py-12 md:py-16 bg-white border-t border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="text-sm font-semibold text-[var(--brand)] uppercase tracking-wider block mb-2">
                Eksklusive Rabataftaler
              </span>
              <h2 className="text-3xl font-semibold text-slate-900 mb-4">
                Spar penge på din billeje
              </h2>
              <p className="text-lg text-slate-600">
                Vi har forhandlet fordelagtige rabataftaler på plads med udvalgte biludlejningsfirmaer. Du kan benytte nedenstående rabatkoder direkte hos udbyderne.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Cargest */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-slate-50 rounded-2xl border border-slate-200 p-6 md:p-8 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow"
              >
                <div>
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <img 
                        src="https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/e1634430-2ffa-46ba-8404-97e54c589a3c.jpg" 
                        alt="Cargest Logo" 
                        className="h-10 object-contain mb-4"
                      />
                      <p className="text-sm text-slate-500">Professionel biludlejning i Málaga</p>
                    </div>
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-sm font-semibold shrink-0">
                      5% Rabat
                    </span>
                  </div>

                  <p className="text-slate-600 mb-6 leading-relaxed">
                    Cargest tilbyder et bredt udvalg af biler direkte fra Málaga lufthavn med høj service og gennemskuelige lejevilkår.
                  </p>

                  <div className="bg-white rounded-xl p-4 border border-slate-100 mb-6">
                    <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Rabatkode</div>
                    <div className="flex items-center justify-between">
                      <code className="text-lg font-mono font-bold text-[var(--brand)] tracking-wider">JMN2021</code>
                      <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">Brug ved booking</span>
                    </div>
                  </div>
                </div>

                <a
                  href="https://www.cargest.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 w-full py-3 px-4 bg-[var(--brand)] hover:bg-[var(--brand-hover,slate-800)] text-white font-semibold rounded-xl transition-colors"
                >
                  Besøg Cargest
                  <ExternalLink size={16} />
                </a>
              </motion.div>

              {/* Dan Biludlejning */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-slate-50 rounded-2xl border border-slate-200 p-6 md:p-8 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow"
              >
                <div>
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <img 
                        src="https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/c6675929-2698-4552-850a-06846f3780ad.jpg" 
                        alt="Dan Biludlejning Logo" 
                        className="h-10 object-contain mb-4"
                      />
                      <p className="text-sm text-slate-500">Dansk-ejet biludlejning i Spanien</p>
                    </div>
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-sm font-semibold shrink-0">
                      Op til 20% Rabat
                    </span>
                  </div>

                  <p className="text-slate-600 mb-6 leading-relaxed">
                    Perfekt til længerevarende ophold. Få ekstra store rabatter, når du lejer bil i 4 uger eller mere.
                  </p>

                  <div className="space-y-3 mb-6">
                    <div className="bg-white rounded-xl p-4 border border-slate-100">
                      <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Rabatbetingelser</div>
                      <div className="text-slate-800 font-medium text-sm">
                        Rabat ved +4 uger: <span className="text-emerald-600 font-bold">-15%</span>
                        <br />
                        Rabat ved +8 uger: <span className="text-emerald-600 font-bold">-20%</span>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-slate-100">
                      <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Rabatkode & fremgangsmåde</div>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <code className="text-lg font-mono font-bold text-[var(--brand)] tracking-wider">Golf4Life</code>
                          <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded border border-amber-100 font-medium">Vigtigt!</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Koden skal skrives i <strong>kommentarfeltet</strong> ved bestilling.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <a
                  href="https://danbiludlejning.dk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 w-full py-3 px-4 bg-[var(--brand)] hover:bg-[var(--brand-hover,slate-800)] text-white font-semibold rounded-xl transition-colors"
                >
                  Besøg Dan Biludlejning
                  <ExternalLink size={16} />
                </a>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Helpful Tips Section */}
        <section className="py-12 bg-white border-t border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl font-semibold text-slate-900 mb-4">
                Gode råd til billeje i Spanien
              </h2>
              <p className="text-lg text-slate-600">
                For at sikre dig en problemfri oplevelse, har vi samlet en række vigtige punkter, du bør være opmærksom på, når du booker din bil.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {tips.map((tip, index) => (
                <motion.div
                  key={tip.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-slate-50 p-6 md:p-8 rounded-2xl border border-slate-100 flex gap-4"
                >
                  <div className="text-[var(--brand)] shrink-0 mt-1">
                    <CheckCircle size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{tip.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{tip.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Airport & Pick-up Info */}
        <section className="py-16 md:py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-sm font-semibold text-[var(--brand)] uppercase tracking-wider block mb-4">
                  Ankomst & Afhentning
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  Hvor skal jeg afhente bilen?
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  De fleste af vores gæster vælger at flyve til <strong>Málaga Lufthavn (AGP)</strong>. Vi anbefaler at benytte vores samarbejdspartnere <strong>Cargest</strong> eller <strong>Dan Biludlejning</strong>, som vi har fordelagtige rabataftaler med, for at sikre dig den bedste service og pris.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="p-1 bg-emerald-100 text-emerald-800 rounded-md shrink-0 mt-1">
                      <Info size={16} />
                    </div>
                    <p className="text-slate-600">
                      <strong>Málaga Lufthavn:</strong> Ca. 2 timers kørsel til vores lejligheder i Cádiz. Stort udvalg og ofte de billigste priser.
                    </p>
                  </div>
                </div>
                <a 
                  href="/kontakt" 
                  className="inline-flex items-center gap-2 text-[var(--brand)] font-semibold hover:gap-3 transition-all"
                >
                  Har du spørgsmål? Kontakt os gerne
                  <ArrowRight size={20} />
                </a>
              </div>

              <div className="rounded-2xl overflow-hidden shadow-xl aspect-[4/3]">
                <img 
                  src="https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/169886b3-d834-4503-8ba3-76f6cc004864.jpg" 
                  alt="Costa de la Luz kystvej" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BillejePage;
